import React, { useEffect, useState } from "react";
import { FiBell } from "react-icons/fi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (isNaN(date)) return String(dateStr);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const NotificationHistoryTab = ({ userId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterType, setFilterType] = useState("all");
  const limit = 10;

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, page]);

  const fetchHistory = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/notification/user-list?userId=${userId}&page=${page}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.status === true) {
        setHistory(result.data || []);
        setTotal(result.pagination?.total || result.total || 0);
      } else {
        throw new Error(result.message || "Failed to fetch notification history");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && history.length === 0) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && history.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 text-sm">
        {error}
      </div>
    );
  }

  if (history.length === 0) {
    return <p className="text-sm text-gray-400">No notifications found for this user.</p>;
  }

  const displayHistory = filterType === "admin"
    ? history.filter((noti) => !noti.type || noti.type.toLowerCase() === "manual")
    : history;

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setFilterType("all")}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
            filterType === "all"
              ? "bg-blue-600 text-white shadow-sm"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          All Notifications
        </button>
        <button
          onClick={() => setFilterType("admin")}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
            filterType === "admin"
              ? "bg-blue-600 text-white shadow-sm"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          Sent by Admin
        </button>
      </div>

      <div className="overflow-x-auto -mx-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
              <th className="px-6 py-2.5">Image</th>
              <th className="px-6 py-2.5">Title & Message</th>
              <th className="px-6 py-2.5">Redirect</th>
              <th className="px-6 py-2.5">Type</th>
              <th className="px-6 py-2.5 whitespace-nowrap">Sent Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {displayHistory.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-8 text-center text-sm text-gray-400">
                  {filterType === "admin" ? "No admin notifications found on this page." : "No notifications found."}
                </td>
              </tr>
            ) : (
              displayHistory.map((noti) => {
                const imgSrc = noti.imageUrl || noti.image || null;
                return (
                  <tr key={noti.id} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-3.5">
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt="banner"
                        className="w-10 h-10 rounded-lg object-cover border border-gray-100"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                        <FiBell size={14} className="text-gray-300" />
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-3.5 max-w-xs">
                    <p className="font-medium text-gray-800 line-clamp-1">{noti.title}</p>
                    <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{noti.message}</p>
                  </td>
                  <td className="px-6 py-3.5">
                    {noti.redirectRoute ? (
                      <span className="inline-block bg-blue-50 text-blue-500 text-xs font-mono px-2 py-0.5 rounded">
                        {noti.redirectRoute}
                      </span>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-6 py-3.5 capitalize text-gray-600">
                    {noti.type || "manual"}
                  </td>
                  <td className="px-6 py-3.5 text-gray-500 text-xs whitespace-nowrap">
                    {formatDate(noti.createdAt)}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 items-center mt-5">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            className="px-3 py-1.5 border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 text-xs font-medium transition"
          >
            &laquo; Prev
          </button>
          <span className="text-xs text-gray-500 font-medium">
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            className="px-3 py-1.5 border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 text-xs font-medium transition"
          >
            Next &raquo;
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationHistoryTab;
